import React, { useContext, useEffect, useState } from 'react';
import {
  ComponentRendering,
  Field,
  Placeholder,
  RichText,
  SitecoreContext,
  withSitecoreContext,
  WithSitecoreContextProps,
} from '@sitecore-jss/sitecore-jss-react';
import './index.scss';
import CreditCard from '../CreditCard/CreditCard';
import { APIWebstoreContext } from '../../context/globalContext';
import { getAPICall } from '../../dataFetcher/axiosMethodCalls';
import BillingAddress from './BillingAddress';
import { ProcessOrderValidationService } from '../CheckoutPage/ProcessOrderValidationService';
import nextId from 'react-id-generator';
import { getCountryConfigFields } from '../../utils/countryConfigurationHelper';
import { PaymentTypes } from '../../utils/PaymentTypes';
import { LOB_LIST } from '../../context/reducer';
import NonInstantPayment from '../NonInstantPayment/NonInstantPayment';
import { ICP_IDENTIFIER, PAYMENT_TYPES_ORDER } from '../../utils/constants';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
type PaymentMethodProps = WithSitecoreContextProps & {
  fields: {
    CCVTooltip: Field<string>;
    WireTransferTooltip: Field<string>;
    CheckTooltip: Field<string>;
    BillingAddressInfo: Field<string>;
    PaymentTypes: Array<PaymentTypesFieldProps>;
    NoCostPaymentMessage: Field<string>;
    Countries: Array<CountryConfigurationFieldProps>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  rendering: ComponentRendering;
  invoiceIdentifier?: string;
};

export type PaymentTypesFieldProps = {
  displayName: string;
  fields: IPaymentTypePropsFields;
};

export interface IPaymentTypePropsFields {
  PaymentTypeName: Field<string>;
  PaymentTypeValue: Field<string>;
  MinimumOrderTotal: Field<number>;
  'Tooltip Text': Field<string>;
}

export type CountryConfigurationFieldProps = {
  displayName: string;
  fields: ICountryPropsFields;
};

export interface ICountryPropsFields {
  CountryCode: Field<string>;
  HidePaymentType: Field<boolean>;
  CountryName: Field<string>;
  PaymentSectionMessage: Field<string>;
}

const PaymentMethod = ({
  fields,
  sitecoreContext,
  rendering,
  invoiceIdentifier,
}: PaymentMethodProps): React.JSX.Element => {
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [orderTotal, setOrderTotal] = useState<number | undefined>();
  const [invoiceTotal, setInvoiceTotal] = useState<number | undefined>();
  const [selectedPaymentType, setselectedPaymentType] = useState('');
  const [countryConfigurationInfo, setCountryConfigurationInfo] = useState<ICountryPropsFields>();
  const [CountryList, setCountryList] = useState<{ country: string; countryCode: string }[]>([]);
  const [country, setCountry] = useState('US');

  function handleCountryChange(selected: string) {
    setCountry(selected);

    if (invoiceIdentifier) {
      const countryConfig = getCountrySpecificConfiguration(selected);
      setCountryConfigurationInfo(countryConfig);

      if (dispatch) {
        dispatch({
          type: 'INVOICE_UPDATE',
          payload: {
            groupAdminCountry: selected,
          },
        });
      }
    }
  }

  useEffect(() => {
    getCountryListApiCall();
  }, []);

  function getCountryListApiCall() {
    getAPICall(`${process.env.REACT_APP_PROFILE_URL}/countries`).then(function (response: any) {
      if (response?.data) {
        const list = Object.keys(response.data).map((key) => ({
          country: response.data[key]?.api_name,
          countryCode: response.data[key]?.api_isocountrycode,
        }));
        setCountryList(list);
      }
    });
  }

  useEffect(() => {
    if (
      Object.values(PaymentTypes).includes(selectedPaymentType as PaymentTypes) &&
      !getPaymentTypes()?.some(
        (type) => type.fields?.PaymentTypeName?.value === selectedPaymentType
      )
    ) {
      setselectedPaymentType('');

      if (dispatch) {
        dispatch({
          type: 'CHECKOUT_UPDATE',
          payload: {
            paymentType: 'None',
            orderDetails: Object.fromEntries(
              Object.entries(state?.orderDetails || {}).map(([key, details]) => [
                key,
                {
                  ...details,
                  paymentType:
                    details.paymentType === selectedPaymentType ? 'None' : details.paymentType,
                },
              ])
            ),
          },
        });
      }
    }
  }, [orderTotal]);

  useEffect(() => {
    ProcessOrderValidationService.setCCInfo({}, false);
    if (dispatch) {
      const orderDetails = Object.fromEntries(
        LOB_LIST.map((lob) => [
          lob,
          {
            paymentType: 'None',
            orderIds: [],
          },
        ])
      );
      dispatch({
        type: 'CHECKOUT_UPDATE',
        payload: {
          paymentType: 'None',
          orderDetails,
        },
      });
    }
  }, []);

  useEffect(() => {
    setOrderTotal(state?.orderTotals?.totalAmount);
  }, [state?.orderTotals?.totalAmount]);

  useEffect(() => {
    const total = state?.invoicesSummary?.[invoiceIdentifier ?? '']?.totalAmount;
    setInvoiceTotal(total);
  }, [state?.invoicesSummary, invoiceIdentifier]);

  useEffect(() => {
    setselectedPaymentType(selectedPaymentType ?? '');
  }, [state?.paymentType]);

  useEffect(() => {
    if (!invoiceIdentifier) {
      getShippingCountry();
    }
  }, [state?.toggleCalculateOrder]);

  function getShippingCountry() {
    let shippingCountry: string = '';
    getAPICall(`${process.env.REACT_APP_CART_URL}/checkout/getshippingcountry`)
      .then(function (response: any) {
        if (response && response.data) {
          shippingCountry = response.data;
        }
      })
      .finally(() => {
        if (!shippingCountry && shippingCountry.length === 0) {
          shippingCountry = ProcessOrderValidationService.getShippingInfo()?.shippingInfo?.country;
        }
        setCountryConfigurationInfo(getCountrySpecificConfiguration(shippingCountry));
      });
  }

  function getCountrySpecificConfiguration(shippingCountry: string) {
    let countryConfigurationData: Array<CountryConfigurationFieldProps> = [];
    countryConfigurationData = getCountryConfigFields(shippingCountry, fields?.Countries);
    const countryConfigurationFields: ICountryPropsFields | undefined =
      countryConfigurationData.map((config) => {
        return {
          CountryCode: config?.fields?.CountryCode,
          HidePaymentType: config?.fields?.HidePaymentType,
          CountryName: config?.fields?.CountryName,
          PaymentSectionMessage: config?.fields?.PaymentSectionMessage,
        };
      })[0];
    return countryConfigurationFields;
  }

  function InvoiceSection(type: string, identifier: string) {
    setselectedPaymentType(type);
    if (dispatch) {
      dispatch({
        type: 'INVOICE_UPDATE',
        payload: {
          invoicePayment: {
            [identifier]: type,
          },
        },
      });
    }
  }

  function PaymentSection(type: string) {
    setselectedPaymentType(type);
    if (dispatch) {
      const updatedOrderDetails = Object.fromEntries(
        LOB_LIST.map((lob) => {
          const existing = state?.orderDetails?.[lob];
          const existingPaymentType = Object.values(PaymentTypes).includes(
            existing?.paymentType as PaymentTypes
          )
            ? null
            : existing?.paymentType;
          return [
            lob,
            {
              orderIds: existing?.orderIds ?? [],
              paymentType:
                existingPaymentType && existingPaymentType !== 'None' ? existingPaymentType : type,
            },
          ];
        })
      );
      dispatch({
        type: 'CHECKOUT_UPDATE',
        payload: {
          orderDetails: updatedOrderDetails,
          paymentType: type,
        },
      });
    }
  }

  function getPaymentTypes(): Array<PaymentTypesFieldProps> | undefined {
    if (fields?.PaymentTypes) {
      let filteredPaymentTypes = fields?.PaymentTypes?.filter(
        (types) => ((orderTotal || invoiceTotal) ?? 0) >= types?.fields?.MinimumOrderTotal?.value
      );
      return filteredPaymentTypes.sort(
        (a, b) =>
          PAYMENT_TYPES_ORDER.indexOf(a.displayName) - PAYMENT_TYPES_ORDER.indexOf(b.displayName)
      );
    }
    return;
  }

  function getPaymentTypeHeader() {
    if (
      (orderTotal === 0 && !invoiceIdentifier && !state?.isGroupPaymentSuccessful) ||
      (invoiceIdentifier && invoiceTotal === 0)
    ) {
      if (fields?.NoCostPaymentMessage) {
        return <RichText tag="p" field={fields?.NoCostPaymentMessage} />;
      } else {
        return <p>No Payment Required</p>;
      }
    } else if (
      countryConfigurationInfo?.PaymentSectionMessage &&
      ((!invoiceIdentifier && orderTotal !== 0) || (invoiceIdentifier && invoiceTotal !== 0))
    ) {
      return <RichText tag="p" field={countryConfigurationInfo?.PaymentSectionMessage} />;
    } else {
      return <p>Select Payment Type</p>;
    }
  }

  return (
    <div className={`paymentmethod ${invoiceIdentifier ? 'invoice' : ''}`}>
      {sitecoreContext.pageEditing && (
        <>
          <p>PaymentMethod Component Edit Mode</p>
        </>
      )}
      {!invoiceIdentifier && <hr />}
      <div className="checkoutPaymentSection">
        <div className="paymentArea">
          {!invoiceIdentifier && <div className="SNo">2</div>}

          <div className="Title">
            <p>Payment Method</p>
          </div>
        </div>
        {invoiceIdentifier && (state?.invoiceCounts?.totalCount ?? 0) > 0 && (
          <div className="countrydiv">
            <div className="street-header-div">
              <span className="addressinfoheader">Select Country</span>
            </div>
            <select
              name="country"
              className="inputtext"
              value={country}
              onChange={(e) => handleCountryChange(e.target.value)}
            >
              {CountryList.map((item) => (
                <option key={item.countryCode} value={item.countryCode}>
                  {item.country}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className={`paymentType ${invoiceIdentifier ? 'invoice' : ''}`}>
          {getPaymentTypeHeader()}
        </div>

        <div className={`paymentSelection ${invoiceIdentifier ? 'invoice' : ''}`}>
          {!countryConfigurationInfo?.HidePaymentType?.value &&
            getPaymentTypes()?.map((paymentType) => (
              <React.Fragment key={paymentType?.fields?.PaymentTypeName?.value}>
                <div className="radiodiv">
                  <input
                    type="radio"
                    id={paymentType?.fields?.PaymentTypeName?.value}
                    name="paymenttype"
                    className="radiobutton"
                    checked={selectedPaymentType === paymentType?.fields?.PaymentTypeName?.value}
                    onChange={() =>
                      invoiceIdentifier
                        ? InvoiceSection(
                            paymentType?.fields?.PaymentTypeName?.value,
                            invoiceIdentifier
                          )
                        : PaymentSection(paymentType?.fields?.PaymentTypeName?.value)
                    }
                  ></input>
                  <div className="paymentOptionContainer">
                    <span
                      className="spanpaymenttype"
                      onClick={() =>
                        invoiceIdentifier
                          ? InvoiceSection(
                              paymentType?.fields?.PaymentTypeName?.value,
                              invoiceIdentifier
                            )
                          : PaymentSection(paymentType?.fields?.PaymentTypeName?.value)
                      }
                    >
                      {' '}
                      {paymentType?.fields?.PaymentTypeValue?.value}
                    </span>
                    <CustomTooltip
                      fields={{ 'Tooltip Text': paymentType?.fields?.['Tooltip Text'] }}
                    />
                  </div>
                </div>
                {selectedPaymentType === PaymentTypes.CreditCard &&
                  paymentType?.fields?.PaymentTypeName?.value === PaymentTypes.CreditCard && (
                    <div className="creditdiv">
                      <CreditCard
                        fields={{
                          CCVTooltip: fields?.CCVTooltip,
                        }}
                        invoiceIdentifier={invoiceIdentifier}
                      ></CreditCard>
                      <BillingAddress
                        fields={{ BillingAddressInfo: fields?.BillingAddressInfo }}
                        invoiceIdentifier={invoiceIdentifier}
                      ></BillingAddress>
                      <br></br>
                    </div>
                  )}
                {paymentType?.fields?.PaymentTypeName?.value === selectedPaymentType &&
                  ((state?.cartCounts?.lobCounts?.ICP ?? 0) > 0 ||
                    invoiceIdentifier === ICP_IDENTIFIER) &&
                  selectedPaymentType !== PaymentTypes.CreditCard && (
                    <NonInstantPayment
                      paymentType={selectedPaymentType}
                      invoiceIdentifier={invoiceIdentifier || ''}
                    />
                  )}
              </React.Fragment>
            ))}
          {state?.hardCopyQuantity !== null &&
            (state?.cartCounts?.lobCounts?.ICP ?? 0) > 0 &&
            state?.cartCounts?.lobCounts?.ICP !== state?.hardCopyQuantity && (
              <Placeholder name="group_payment" rendering={rendering} />
            )}
        </div>
      </div>
    </div>
  );
};
export default withSitecoreContext()(PaymentMethod);
